// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@synd/App/Components/Notify";
import createEditRetailersHandler from "@synd/App/Handlers/RetailerManagement/CreateEditRetailers";
import deactivateRetailerHandler from "@synd/App/Handlers/RetailerManagement/DeactivateRetailer";
import loadRetailers from "@synd/App/Handlers/RetailerManagement/LoadRetailers";
import retailsModalOpenHandler from "@synd/App/Handlers/RetailerManagement/RetailsModalOpenHandler";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { getRetailerUser } from "@synd/App/Store/Selectors/RetailerManagementSelector";
import DynamicDropdown, { OptionObject } from "@synd/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import TextField from "@synd/MobiusOverrides/TextField";
import { placeHolders } from "@synd/XDL/constants";
import LinkDeactivateUser from "@synd/XDL/Icons/LinkDeactivateUser";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import {
    applyMask,
    color,
    createLinkIconStyles,
    mergeWithCss,
    modalButtonsActionStyles,
    useValidate,
    xDefaultRules,
    xMask,
    xScreen,
} from "@synd/XDL/Utilities";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: CustomAppState) => {
    const userId = state.xRetailerManagement?.userId;
    const isOpen = state.xRetailerManagement?.isOpen;
    const isEditMode = state.xRetailerManagement?.isEditMode;
    return {
        retailUsersDataView: getRetailerUser(state, userId),
        userId,
        isOpen,
        isEditMode,
        countries: getCurrentCountries(state),
        RetailerPricingManagementPageUri: getPageLinkByPageType(state, "RetailerPricingManagementPage")?.url,
    };
};

const mapDispatchToProps = {
    loadCurrentCountries,
    loadRetailers,
    retailsModalOpenHandler,
    createEditRetailersHandler,
    deactivateRetailerHandler,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CreateEditRetailUserModalStyles {
    modal?: ModalPresentationProps;
    userInfoReadOnlyWrapper?: InjectableCss;
    form?: InjectableCss;
    inputsWrapper?: InjectableCss;
    bottomSection?: InjectableCss;
    buttonWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    deactivateUserLink?: LinkPresentationProps;
    modalHint?: TypographyPresentationProps;
    stateDropdownWrapper?: InjectableCss;
    stateSelect?: SelectPresentationProps;
}

export const createEditRetailUserModalStyles: CreateEditRetailUserModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    form: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    inputsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 12px 22px;
            > div {
                flex: 0 1 calc(50% - 11px);
                &:has([class^="PopoverTriggerInput"], .full-width-input) {
                    flex: 1 1 100%;
                }
                ${xScreen("lte", "xs")} {
                    flex: 1 1 100%;
                }
            }
        `,
    },
    stateDropdownWrapper: {
        css: css`
            &&& {
                flex: 0 1 calc(50% - 11px);

                ${xScreen("lte", "xs")} {
                    flex: 0 0 100%;
                }
            }
        `,
    },
    stateSelect: {
        css: css`
            &&& {
                &[data-selected-index=""] {
                    color: ${color("text.main", 0.33)};
                    font-style: italic;
                    option {
                        font-style: normal;
                        &:not([disabled]) {
                            color: ${color("text.main")};
                        }
                    }
                }
            }
        `,
    },
    userInfoReadOnlyWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    bottomSection: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding-top: 16px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    buttonWrapper: {
        ...mergeWithCss(modalButtonsActionStyles.buttonsGridItem, {
            css: css`
                border-top: unset;
            `,
        }),
    },
    submitButton: {
        ...modalButtonsActionStyles.submitButton,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
    deactivateUserLink: {
        ...createLinkIconStyles({
            icon: LinkDeactivateUser,
            sizeVariant: "small",
            variant: "primary",
            linkStyles: {
                icon: {
                    position: "left",
                    iconProps: {
                        size: 14,
                    },
                },
                css: css`
                    width: fit-content;
                `,
            },
        }),
    },
    modalHint: {
        size: fontConfig.small.size,
        lineHeight: fontConfig.small.lineHeight,
        css: css`
            display: block;
            margin-bottom: 16px;
        `,
    },
};

const styles = createEditRetailUserModalStyles;

const StyledForm = getStyledWrapper("form");

const CreateEditRetailUserModal = ({
    isEditMode,
    isOpen,
    retailUsersDataView,
    userId,
    countries,
    loadCurrentCountries,
    loadRetailers,
    retailsModalOpenHandler,
    createEditRetailersHandler,
    deactivateRetailerHandler,
    history,
    RetailerPricingManagementPageUri,
}: Props) => {
    const [country, setCountry] = useState<CountryModel>();
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedRetailer, setSelectedRetailer] = useState("");
    const [options, selectOptions] = useState<OptionObject[]>([]);
    const [isLoadRetailers, setIsLoadRetailers] = useState(false);
    const [isDeactivatingRetailer, setIsDeactivatingRetailer] = useState(false);
    const [createNewRetailer, setCreateNewRetailer] = useState(false);
    const [confirmModalIsOpen, setConfirmModalIsOpen] = useState<boolean>(false);

    const toaster = useToaster();

    const { masksList, setFieldValue, values, field, handleSubmit, isSubmitting, errors, resetForm } = useValidate({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            retailerId: null,
            retailerName: "",
            city: "",
            stateId: "",
        },
        validators: {
            firstName: `required|${xDefaultRules.firstName}`,
            lastName: `required|${xDefaultRules.lastName}`,
            email: isEditMode ? `${xDefaultRules.email}` : `required|${xDefaultRules.email}`,
            phone: `required|${xDefaultRules.phone}`,
            retailerName: isEditMode ? `${xDefaultRules.text}` : `required|${xDefaultRules.text}`,
            city: isEditMode ? `${xDefaultRules.text}` : `required|${xDefaultRules.city}`,
            stateId:
                country?.states && country?.states?.length > 0 && !isEditMode
                    ? `required|${xDefaultRules.text}`
                    : `${xDefaultRules.text}`,
        },
        onSubmit: (fieldValues: any, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    // Load countries if not available and set selected country
    useEffect(() => {
        if (!isOpen || isEditMode) {
            return;
        }
        if (!countries) {
            loadCurrentCountries();
        }
        const selectedCountry = countries?.find(o => o.abbreviation.toLowerCase() === "us");
        setCountry(selectedCountry);
    }, [countries, isEditMode, isOpen]);

    useEffect(() => {
        if (!isOpen || !isEditMode) {
            return;
        }
        if (retailUsersDataView.value) {
            const data = retailUsersDataView.value;
            setFieldValue("firstName", data.firstName);
            setFieldValue("lastName", data.lastName);
            setFieldValue("email", data.email);
            setFieldValue("retailerName", data.retailerName);
            setFieldValue("phone", xMask.phoneNumber(data.phone));
            setFieldValue("city", data.city);
            setFieldValue("stateId", data.stateId);
            setSelectedRetailer(data.retailerName);
        }
    }, [retailUsersDataView, isEditMode, isOpen]);

    // Load Retailers
    useEffect(() => {
        if (!isOpen || isEditMode || options.length > 0) {
            return;
        }
        setIsLoadRetailers(true);
        loadRetailers({
            filterToWholesaler: false,
            onSuccess: result => {
                setIsLoadRetailers(false);

                if (result) {
                    const options: OptionObject[] = result?.retailers.map(o => ({
                        optionText: o.name,
                        optionValue: o.id,
                    }));

                    selectOptions([
                        { optionText: "Create New Retailer", optionValue: "createNewRetailer" },
                        ...options,
                    ]);
                }
            },
        });
    }, [options, isEditMode, isOpen]);

    const handleCloseModal = () => {
        resetForm();
        setSelectedRetailer("");
        setCreateNewRetailer(false);
        retailsModalOpenHandler({ isOpen: false });
        setErrorMessage("");
    };

    const submitHandler = (fieldValues: any, setSubmitting: any) => {
        setSubmitting(true);
        if (Object.keys(errors).length > 0) {
            setSubmitting(false);
            return;
        }
        createEditRetailersHandler({
            isEditMode,
            id: userId,
            ...fieldValues,
            onSuccess: result => {
                setSubmitting(false);
                setErrorMessage("");
                toaster.addToast({
                    body: isEditMode
                        ? siteMessage("Custom_Edit_Retail_User_Success")
                        : siteMessage("Custom_Creat_Retail_User_Success"),
                    messageType: "success",
                });
                handleCloseModal();
                if (!isEditMode && RetailerPricingManagementPageUri && result.retailerId) {
                    history.push(`${RetailerPricingManagementPageUri}?retailerId=${result.retailerId}`);
                }
            },
            onError: (error: string) => {
                setSubmitting(false);
                setErrorMessage(error);
            },
        });
    };

    const handleOpenConfirmationModal = () => {
        setConfirmModalIsOpen(true);
    };

    const handleCloseConfirmationModal = () => {
        setConfirmModalIsOpen(false);
    };

    const handleDeactivateRetailer = () => {
        if (!userId) {
            return;
        }
        setIsDeactivatingRetailer(true);
        deactivateRetailerHandler({
            id: userId,
            onSuccess: () => {
                setIsDeactivatingRetailer(false);
                handleCloseConfirmationModal();
                toaster.addToast({
                    body: siteMessage("Custom_Deactivate_Retail_User_Success"),
                    messageType: "success",
                });
                handleCloseModal();
            },
            onError: () => {
                setIsDeactivatingRetailer(false);
                handleCloseConfirmationModal();
                toaster.addToast({
                    body: siteMessage("Custom_Deactivate_Retail_User_Failer"),
                    messageType: "danger",
                });
            },
        });
    };

    // Checking when creating a new retailer user if all required fields are filled
    const isCreateButtonDisabled = useMemo(() => {
        if (isEditMode) {
            return isSubmitting;
        }
        const hasErrors = Object.keys(errors).length > 0;

        const requiredFieldsFilled =
            values.firstName?.trim() &&
            values.lastName?.trim() &&
            values.email?.trim() &&
            values.phone?.trim() &&
            values.retailerName?.trim() &&
            values.city?.trim() &&
            (!country?.states || country.states.length === 0 || values.stateId?.trim());

        return isSubmitting || hasErrors || !requiredFieldsFilled;
    }, [isEditMode, isSubmitting, errors, values, country]);

    return (
        <>
            <Modal
                {...styles.modal}
                headline={translate(isEditMode ? "Edit Retailer Details" : "Create New Retail User")}
                isOpen={isOpen}
                data-test-selector="Create Edit Retailer User Modal"
                handleClose={handleCloseModal}
            >
                <Typography {...styles.modalHint}>
                    {siteMessage(isEditMode ? "Custom_Edit_Retailer_Modal_Hint" : "Custom_Create_Retailer_Modal_Hint")}
                </Typography>
                <StyledForm {...styles.form} noValidate onSubmit={handleSubmit}>
                    <StyledWrapper {...styles.inputsWrapper}>
                        <TextField
                            {...field("firstName")}
                            label={translate("First name")}
                            placeholder={translate("First name")}
                            required={true}
                        />
                        <TextField
                            {...field("lastName")}
                            label={translate("Last Name")}
                            placeholder={translate("Last Name")}
                            required={true}
                        />
                        <TextField
                            {...field("email")}
                            type="email"
                            label={translate("Email")}
                            placeholder={translate("Email")}
                            disabled={isEditMode}
                            {...(!isEditMode && { required: true })}
                        />
                        <TextField
                            {...field("phone")}
                            type="phone"
                            label={translate("Phone number")}
                            placeholder={placeHolders.phone}
                            error={field("phone").error && "Phone number is a required field"}
                            required={true}
                        />
                        <DynamicDropdown
                            {...field("retailerName")}
                            label={translate("Retailer Name")}
                            placeholder={translate("Select Existing or Create New Retailer")}
                            disabled={isEditMode}
                            options={options}
                            isLoading={isLoadRetailers}
                            onSelectionChange={(v, t) => {
                                const key = "retailerName";
                                const oldValue = values[key];
                                const newValue = v || "";
                                const masked = applyMask(masksList[key], newValue, oldValue);
                                if (masked !== "createNewRetailer") {
                                    setFieldValue("retailerId", masked);
                                    setFieldValue(key, t);
                                    setCreateNewRetailer(false);
                                } else {
                                    setCreateNewRetailer(true);
                                    setFieldValue("retailerId", null);
                                    setFieldValue(key, "");
                                }
                            }}
                            search={selectedRetailer}
                            appendTo="body"
                            error={
                                field("retailerName").error && !createNewRetailer && "Retailer Name is a required field"
                            }
                            {...(!isEditMode && { required: true })}
                        />

                        {createNewRetailer && (
                            <TextField
                                {...field("retailerName")}
                                label={translate("New Retailer Name")}
                                placeholder={translate("New Retailer Name")}
                                className="full-width-input"
                                required={true}
                            />
                        )}

                        <TextField
                            {...field("city")}
                            label={translate("City")}
                            placeholder={translate("City")}
                            disabled={isEditMode}
                            {...(!isEditMode && { required: true })}
                        />

                        {((country && country?.states && !!country.states?.length) || isEditMode) && (
                            <Select
                                {...field("stateId")}
                                {...styles.stateSelect}
                                label={translate("State")}
                                disabled={isEditMode}
                                error={field("stateId").error && "State is a required field"}
                                {...(!isEditMode && { required: true })}
                            >
                                <option value="" disabled>
                                    {translate("Select State")}
                                </option>
                                {country?.states?.map((o: any) => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}
                                    </option>
                                ))}
                            </Select>
                        )}
                    </StyledWrapper>

                    <StyledWrapper {...styles.bottomSection}>
                        {isEditMode && (
                            <Link
                                {...styles.deactivateUserLink}
                                disabled={isDeactivatingRetailer}
                                onClick={handleOpenConfirmationModal}
                                type="button"
                            >
                                {translate("Deactivate User")}
                            </Link>
                        )}
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Button {...styles.cancelButton} onClick={handleCloseModal}>
                                {translate("Cancel")}
                            </Button>
                            <Button disabled={isCreateButtonDisabled} {...styles.submitButton} type="submit">
                                {translate(isEditMode ? "Save" : "Create")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    {errorMessage && <Notify type="danger">{errorMessage}</Notify>}
                </StyledForm>
            </Modal>
            <TwoButtonModal
                headlineText={siteMessage("Custom_Deactivate_Retail_User_Title")}
                {...styles.modal}
                modalIsOpen={confirmModalIsOpen}
                messageText={siteMessage("Custom_Deactivate_Retail_User_Message")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Deactivate")}
                onCancel={handleCloseConfirmationModal}
                onSubmit={handleDeactivateRetailer}
                onClose={handleCloseConfirmationModal}
                submitTestSelector="submitNavigationMode"
            ></TwoButtonModal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(CreateEditRetailUserModal));
